<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" @show="onShow"
                   :content-in-form="false" @hidden="onHidden" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" scrollable modal-class="mobile-full-screen"
                   :modal-loading="dataLoading">

      <template v-slot:modal-header>
        <div class="d-flex align-items-center">
          <h5 class="modal-title d-inline-block">
            {{ $t('Update Lot') }}
          </h5>
          <span class="ml-2 badge badge-secondary" v-if="model && model.inventory && model.inventory.is_draft">
            {{ $t('Draft') }}
          </span>
        </div>
        <button type="button" :disabled="loading" @click="hideReservationModal" aria-label="Close" class="close">×
        </button>
      </template>


      <div class="row">
        <div class="col-md-12">
          <div class="row mb-3">
            <div class="col-md-12">
              <table class="table table-sm mb-0"
                     v-if="model.inventory && model.inventory.customer">
                <tr>
                  <td class="border-top-0 pl-0">
                    <span class="font-weight-bold">{{ $t('Vendor information: ') }} </span>
                    <router-link tabindex="-1"
                                 :to="{ name: 'customers.dashboard', params: { id: model.inventory.customer.uuid }}">
                      {{ model.inventory.customer.display_name }}
                    </router-link>
                    <span v-if="model.inventory.customer && model.inventory.customer.billing_address"
                          v-html="$options.filters.asAddress(model.inventory.customer.billing_address, false)">
                      </span>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <hr class="mt-0">
        </div>
      </div>

      <div class="row position-relative">
        <div class="col-xl-8 border-right">
          <b-form @submit.prevent="handleSubmit(save)">
            <div class="row">
              <div class="col-md-4" v-if="!enableContinuousAuction">
                <input-widget :disabled="shouldDisable || disableFields.includes('lot_no')" :model="model"
                              attribute="lot_no"/>
              </div>
              <div class="col-md-4" v-if="enableTimedSessions && modal.sale && modal.sale.type === 'timed'">
                <input-widget :model="model" attribute="auction_date_uuid"
                              :rules="model && model.lot_no ? 'required' : ''"
                              display-field="${item.title}" type="select2"
                              :items="modal.sale.dates"/>
              </div>
              <div class="col-md-4">
                <item-location-select-input-widget :model="model" attribute="item_location_uuid"/>
              </div>
              <div class="col-md-4" v-if="model.inventory">
                <department-select-input-widget @select="onDepartmentSelect" :disabled="shouldDisable"
                                                @unselect="onDepartmentUnselect"
                                                :model="model" attribute="department_uuid"/>
              </div>
              <div class="col-md-4" v-if="model.inventory">
                <category-select-input-widget @select="onCategorySelect" get-dynamic-fields-template
                                              :model="model" :disabled="shouldDisable"
                                              :department="model.department" attribute="category_uuid"/>
              </div>
              <div class="col-md-4" v-if="enableInventoryQuantity">
                <input-widget :model="model" attribute="quantity" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4">
                <negotiation-status-select-input-widget :model="model" attribute="negotiation_status"
                                                        :disabled="shouldDisable"/>
              </div>
              <div class="col-md-12">
                <user-select-input-widget :model="model" attribute="specialists_uuids" multiple
                                          :items="model.specialists"
                                          :disabled="shouldDisable || !model.department_uuid"
                                          :filter-extra-data="specialistExtraFilters"/>
              </div>
              <div class="col-md-4" v-if="modal.sale && modal.sale.auction_collections && model.lot_no">
                <auction-collection-select-input-widget :model="model" attribute="auction_collection_uuid"
                                                        :items="modal.sale.auction_collections"
                                                        :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4 hide-lot-checkbox">
                <input-widget :model="model" attribute="hide_lot" type="checkbox" :is-switch="true" size="lg"/>
              </div>
              <div class="col-md-4 hide-lot-checkbox">
                <input-widget :model="model" attribute="use_images_for_auction_gallery" type="checkbox"
                              :is-switch="true" size="lg"
                              :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4 hide-lot-checkbox">
                <input-widget :model="model" attribute="hide_image_from_catalog" type="checkbox" :is-switch="true"
                              size="lg"/>
              </div>
            </div>


            <template v-if="enableContinuousAuction" class="row">

              <div class="row">
                <div v-if="hasArtisioTimedProvider" class="col-md-4 d-flex align-items-center">
                  <input-widget :model="saleModel" attribute="manual_bidder_approval" type="checkbox"/>
                </div>

                <div v-if="hasArtisioTimedProvider" class="col-md-4 d-flex align-items-center">
                  <input-widget :model="saleModel" attribute="require_credit_card_verification" type="checkbox"/>
                </div>

                <div v-if="hasArtisioTimedProvider" class="col-md-4 d-flex align-items-center">
                  <input-widget :model="saleModel" attribute="require_identity_verification" type="checkbox"/>
                </div>
              </div>

              <div class="row">
                <div class="col-md-4 d-flex align-items-center">
                  <input-widget :model="saleModel" attribute="is_initial_deposit_amount" type="checkbox"
                                @change="onDepositAmountChange"/>
                </div>

                <div class="col-md-4 d-flex align-items-center">
                  <input-widget v-if="saleModel.is_initial_deposit_amount" :model="saleModel"
                                attribute="initial_deposit_amount" type="number" placeholder="Amount"
                                :label="false" :rules="saleModel.is_initial_deposit_amount ? 'required' : ''"/>
                </div>
              </div>

            </template>

            <session-section v-if="enableContinuousAuction" :model="model" :disabled="shouldDisable"/>
            <translatable-input-widget v-if="!dynamicFieldsLoading" :disabled="shouldDisable" :hint="false"
                                       skip-disabling-by-type="checkbox"
                                       @onInputChange="onDynamicFieldTranslationInputChange"
                                       :model="model" :fields="model.sorted_dynamic_fields_template" :errors="errors"/>
             <div class="row mb-3" v-if="translationProvider">
                <div class="col-md-12">
                  <b-button variant="primary" @click="requestTranslations" :disabled="loading">
                    <font-awesome-icon icon="language"/>
                      {{$t('Get Translations')}}
                  </b-button>
                </div>
              </div>
            <div class="row">
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_enabled" type="checkbox"
                              :is-switch="true"
                              size="lg" @change="setCollectionInformation"
                              :disabled="shouldDisable"/>
              </div>
            </div>
            <div class="row" v-if="model.collection_info_enabled">
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_1"
                              :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_2"
                              :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_state" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_city" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_zip_code" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_phone" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_email" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_notes" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_first_name" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_last_name" :disabled="shouldDisable"/>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <currency-input-widget :currency="saleCurrency" :model="model"
                                       attribute="low" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4">
                <currency-input-widget :currency="saleCurrency" :model="model"
                                       attribute="high" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4">
                <currency-input-widget :currency="saleCurrency" :model="model"
                                       attribute="reserve" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4">
                <currency-input-widget :currency="saleCurrency" :model="model"
                                       attribute="start_price" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4" v-if="showPricePerUnitField">
                <currency-input-widget :currency="saleCurrency" :model="model"
                                       attribute="reserve_per_unit" :disabled="shouldDisable"/>
              </div>
              <div class="col-md-4">
                <reserve-type-select-input-widget :model="model" attribute="reserve_type" :disabled="shouldDisable"/>
              </div>
            </div>
          </b-form>
        </div>
        <div class="col-xl-4 mb-4">
          <images-view :hide-save-btn="true" :inventory-uuid="model.inventory_uuid" :run-polling-on-save="false"
                       ref="imageViewer" enable-image-edit :disabled="shouldDisable" preserve-deleted-uuids/>
        </div>
      </div>

      <template v-slot:modal-footer>
        <b-button :disabled="disableWithdraw" variant="primary" class="mr-auto" @click="onWithdrawClick"
                  v-permission="'auction_results_auction'">
          {{ $t('Withdraw') }}
        </b-button>


        <template v-if="enableInventoryDraft">
          <cancel-button @click="hideReservationModal" :disabled="disableButtons">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="handleSubmit(onDraftSave)"
                         :disabled="disableButtons ||  shouldDisable" :show-loader="loading">
            {{ $t('Save as Draft') }}
          </submit-button>
          <submit-button @click="handleSubmit(save)" :disabled="disableButtons ||  shouldDisable"
                         :show-loader="loading">
            {{
              model && model.inventory && model.inventory.published_at_least_once ? $t('Save & Publish') : $t('Save')
            }}
          </submit-button>
        </template>
        <template v-else>
          <cancel-button @click="hideReservationModal" :disabled="disableButtons">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="onSubmitWithoutClose()" :disabled="loading" :show-loader="loading">
            {{ $t('Save') }}
          </submit-button>
          <submit-button @click="handleSubmit(save)" :disabled="loading" :show-loader="loading">
            {{ $t('Save & Close') }}
          </submit-button>
        </template>


      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import ReservationModel from "../models/Reservation.model";
  import CancelButton from "../../../core/components/common/CancelButton.vue";
  import SubmitButton from "../../../core/components/common/SubmitButton.vue";
  import CurrencyInputWidget from "../../../core/widgets/CurrencyInputWidget.vue";
  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import ImagesView from "../../inventory/view/images/ImagesView.vue";
  import ItemLocationSelectInputWidget from "../../../core/widgets/ItemLocationSelectInputWidget.vue";
  import TranslatableInputWidget from "../../../core/widgets/TranslatableInputWidget.vue";
  import DepartmentSelectInputWidget from "../../../core/widgets/DepartmentSelectInputWidget.vue";
  import CategorySelectInputWidget from "../../../core/widgets/CategorySelectInputWidget.vue";
  import {createNamespacedHelpers} from "vuex";
  import SessionSection from "../components/SessionSection.vue";
  import NegotiationStatusSelectInputWidget from "@/core/widgets/NegotiationStatusSelectInputWidget.vue";
  import {SOURCE_RESERVATION_DASHBOARD, WS_TRANSLATION_REQUEST} from "@/modules/constants";
  import {ON_RESERVATION_MODAL_SAVE} from "@/plugins/event-bus/event-bus-names";
  import ReserveTypeSelectInputWidget from "@/core/widgets/ReserveTypeSelectInputWidget.vue";
  import AuctionCollectionSelectInputWidget from "@/core/widgets/AuctionCollectionSelectInputWidget.vue";
  import UserSelectInputWidget from "../../../core/widgets/UserSelectInputWidget.vue";
  import SaleModel from "@/modules/sales/Sale.model";
  import SalesService from "@/modules/sales/SalesService";
  import uuidV4 from 'uuid';
  import InventoryService from "@/modules/inventory/InventoryService";

  const {mapState, mapActions} = createNamespacedHelpers('lots');
  const {mapState: inventoryMapState, mapActions: inventoryMapActions} = createNamespacedHelpers('inventory');
  const {mapState: mapStateGlobal, mapGetters: mapGettersGlobal} = createNamespacedHelpers('global');
  const {mapActions: reservationsMapActions} = createNamespacedHelpers('reservations');

  export default {
    name: "ReservationModal",
    components: {
      AuctionCollectionSelectInputWidget,
      ReserveTypeSelectInputWidget,
      UserSelectInputWidget,
      SessionSection,
      NegotiationStatusSelectInputWidget,
      CategorySelectInputWidget, DepartmentSelectInputWidget, TranslatableInputWidget,
      ItemLocationSelectInputWidget, ImagesView, InputWidget, CurrencyInputWidget,
      SubmitButton, CancelButton, ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        requiresTranslation: false,
        model: new ReservationModel(),
        saleModel: new SaleModel(),
        saleCurrency: null,
        dynamicFieldsLoading: false,
      }
    },
    computed: {
      ...mapGettersGlobal(['showPricePerUnitField', 'enableTimedSessions']),
      ...mapState({
        modal: state => state.reservationModal,
        dataLoading: state => state.reservationModal.loading,
        disableFields: state => state.reservationModal.disableFields,
        editObject: state => state.reservationModal.object,
      }),
      ...inventoryMapState({
        images: state => state.view.images
      }),
      ...mapStateGlobal({
        isDynamicFieldsTemplateFromCategory: state => state.isDynamicFieldsTemplateFromCategory,
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        enableContinuousAuction: state => state.enableContinuousAuction,
        enableInventoryDraft: state => state.enableInventoryDraft,
        disableItemEditOnSold: state => state.disableItemEditOnSold,
        enableTimedSessions: state => state.enableTimedSessions,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
        translationProvider: state => state.translationProvider,
      }),
      disableWithdraw() {
        return this.model.status === 'sold' || this.model.status === 'withdrawn' || this.disableFields.includes('withdraw');
      },
      shouldDisable() {
        if (!this.disableItemEditOnSold) {
          return false
        }
        return this.model && this.model.status === 'sold' && this.modal.sale && this.modal.sale.status === 'completed'
      },
      disableButtons() {
        return this.loading || this.dataLoading
      },
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    methods: {
      ...mapActions(['hideReservationModal', 'withdrawItem', 'updateLot', 'getModalObject']),
      ...reservationsMapActions(['getReservation']),
      ...inventoryMapActions(['updateDeletedImages']),
      onShow() {
        this.showConfirmation = true;
        if (this.modal.sale && this.modal.sale.currency) {
          this.saleCurrency = this.modal.sale.currency.symbol;
        }
      },
      onHidden() {
        this.updateDeletedImages([])
        this.model = new ReservationModel();
        this.hideReservationModal();
      },
      onDepartmentSelect(department) {
        // this.dynamicFieldsLoading = true;
        this.model.department = department;
        this.model.category_uuid = null;
        this.model.specialists_uuids = [];
        this.model.specialists = [];
        // this.model.initDynamicFields(this.model, this.model.department.dynamic_fields_template)
        // this.$nextTick(() => {
        //   this.dynamicFieldsLoading = false;
        // })
      },
      onDepartmentUnselect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      onCategorySelect(category) {
        this.dynamicFieldsLoading = true;
        this.model.category = category;
        this.model.category_uuid = category.uuid;
        this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template)
        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })
      },
      onDraftSave() {
        this.save(true)
      },
      async onSubmitWithoutClose() {
        await this.save(false, false)
      },
      async save(is_draft = false, withClose = true) {
        this.loading = true;
        this.model.assignToDynamicFields();

         if (this.requiresTranslation) {
          let thirdBtnText = this.$i18n.t('Submit Without Translation');
          let translationRequest = await this.$confirm(
            this.$i18n.t('It looks like you’ve updated the description in the primary language but haven’t translated these changes into other languages yet.\n' +
              '\n' +
              'Would you like to translate the updated text before continuing?'),
            null,
            null,
            null,
            this.$i18n.t('Untranslated Changes Detected'),
            this.$i18n.t('Translate Now'),
            this.$i18n.t('Cancel'),
            null,
            null,
            null,
            'warning',
            thirdBtnText
          );
          if (translationRequest === true) {
            this.requestTranslations();
            return;

          } else if (translationRequest === false) {
            this.loading = false;
            return;
          }
        }

        if (this.model.status === 'withdrawn' && !this.model.lot_no) {
          this.$errorDialog('You cannot remove lot number for withdrawn lot.')
          return
        }

        if (this.enableContinuousAuction) {
          let responseAuction = await SalesService.partialUpdateSale(this.saleModel.uuid, {
            uuid: this.saleModel.uuid,
            is_initial_deposit_amount: this.saleModel.is_initial_deposit_amount,
            initial_deposit_amount: this.saleModel.initial_deposit_amount,
            require_credit_card_verification: this.saleModel.require_credit_card_verification,
            require_identity_verification: this.saleModel.require_identity_verification,
            manual_bidder_approval: this.saleModel.manual_bidder_approval
          });
          if (!responseAuction.success) {
            this.loading = false;
            if (responseAuction.toastError) {
              this.$errorDialog(responseAuction.toastError);
            } else if (responseAuction.body.non_field_errors) {
              this.$errorDialog(responseAuction.body.non_field_errors[0]);
            } else {
              this.saleModel.setMultipleErrors(responseAuction.body);
            }
            return;
          }
        }

        let modelResponse = await this.updateLot({...this.model.toJSON(), is_draft});

        if (modelResponse.success) {
          let continueLogic = true;
          if (this.images.length > 0) {
            // Returns boolean if images were save in backend
            continueLogic = await this.$refs.imageViewer.save(modelResponse.body.inventory_uuid);
          }
          this.loading = false;
          // Does not continue if image save return false, which means something went wrong
          if (continueLogic) {
            this.showConfirmation = false;
            this.$toast('success', this.$t('Item was successfully updated'));
            if (this.modal.source === SOURCE_RESERVATION_DASHBOARD) {
              await this.getReservation(modelResponse.body.uuid);
            }
            this.$evBus.$emit(ON_RESERVATION_MODAL_SAVE)

            if (withClose) {
              this.hideReservationModal();
            } else {
              this.model = new ReservationModel();
              this.getModalObject({uuid: modelResponse.body.uuid, source: this.modal.source})
            }

          }
        } else {
          this.loading = false;
          if (modelResponse.toastError) {
            this.$errorDialog(modelResponse.toastError);
          } else if (modelResponse.body.non_field_errors) {
            this.$errorDialog(modelResponse.body.non_field_errors[0]);
          } else {
            if (modelResponse.body.dates) {
              for (let index in modelResponse.body.dates) {
                this.model.dates[index].setMultipleErrors(modelResponse.body.dates[index]);
              }
            } else {
              this.model.setMultipleErrors(modelResponse.body);
            }
          }
        }
      },
      async onWithdrawClick() {
        let success = await this.$confirm(this.$i18n.t('Are you sure you want to withdraw following item?'));
        if (success) {
          let response = await this.withdrawItem(this.model.uuid);
          if (response.success) {
            this.$toast('success', this.$i18n.t('Item was successfully withdrawn'));
            this.hideReservationModal();
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      setCollectionInformation(collection_enabled) {
        if (!collection_enabled) {
          this.model.collection_info_address_1 = null;
          this.model.collection_info_address_2 = null;
          this.model.collection_info_state = null;
          this.model.collection_info_city = null;
          this.model.collection_info_zip_code = null;
          this.model.collection_info_phone = null;
          this.model.collection_info_first_name = null;
          this.model.collection_info_last_name = null;
          this.model.collection_info_email = null;
          this.model.collection_info_notes = null;
        } else {
          if (this.model.inventory.customer && this.model.inventory.customer.shipping_address) {
            this.model.collection_info_address_1 = this.model.collection_info_address_1 ? this.model.collection_info_address_1 : this.model.inventory.customer.shipping_address.address_1;
            this.model.collection_info_address_2 = this.model.collection_info_address_2 ? this.model.collection_info_address_2 : this.model.inventory.customer.shipping_address.address_2;
            this.model.collection_info_state = this.model.collection_info_state ? this.model.collection_info_state : this.model.inventory.customer.shipping_address.state;
            this.model.collection_info_city = this.model.collection_info_city ? this.model.collection_info_city : this.model.inventory.customer.shipping_address.city;
            this.model.collection_info_zip_code = this.model.collection_info_zip_code ? this.model.collection_info_zip_code : this.model.inventory.customer.shipping_address.zip_code;
            this.model.collection_info_first_name = this.model.collection_info_first_name ? this.model.collection_info_first_name : this.model.inventory.customer.shipping_address.first_name;
            this.model.collection_info_last_name = this.model.collection_info_last_name ? this.model.collection_info_last_name : this.model.inventory.customer.shipping_address.last_name;
            this.model.collection_info_phone = this.model.collection_info_phone ? this.model.collection_info_phone : this.model.inventory.customer.phone ? this.editObject.inventory.customer.phone.split(',')[0] : null;
            this.model.collection_info_email = this.model.collection_info_email ? this.model.collection_info_email : this.model.inventory.customer.email ? this.editObject.inventory.customer.email : null;
          }
        }
      },
      onDepositAmountChange(val) {
        if (val === false) {
          this.saleModel.initial_deposit_amount = null;
        }
      },
      onDynamicFieldTranslationInputChange(value, locale, field) {
        if (!this.translationProvider) {
          return;
        }
        if (this.translationProvider.from_language === locale && field.translatable
          && ['textarea', 'text', 'ckeditor'].indexOf(field.type) > -1) {
          this.requiresTranslation = true;
        }
      },
      async requestTranslations() {
        this.loading = true;
        const data = [];
        this.model.sorted_dynamic_fields_template.forEach(field => {
          if (field.translatable && ['textarea', 'text', 'ckeditor'].indexOf(field.type) > -1 && this.model[`${field.key}_${this.translationProvider.from_language}`]) {
            data.push({
              key: field.key,
              html: field.type === 'ckeditor',
              value: this.model[`${field.key}_${this.translationProvider.from_language}`]
            });
          }
        });
        const identifier = uuidV4();
        if (!data.length) {
          this.loading = false;
          this.$toast('success', this.$i18n.t('No translations to request'))
          return;
        }

        this.$toast('warning', this.$i18n.t("Pulling translations, please do not close this window or reload the page.."))
        await InventoryService.requestTranslations({'identifier': identifier, 'data': data});

        this.$ws.listen(`inventories.${WS_TRANSLATION_REQUEST}`, (res) => {
          if (res.identifier !== identifier) {
            return;
          }
          if (res.finish) {
            this.loading = false;
            this.requiresTranslation = false;
            this.$ws.remove(`inventories.${WS_TRANSLATION_REQUEST}`)
            this.$toast('success', this.$i18n.t("Translations pulled successfully."))
          } else {
            let language = res.language.toLowerCase().split(/[-_]/)[0];
            this.model[`${res.key}_${language}`] = res.value;
          }

        });
      },
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          if (this.translationProvider) {
            this.requiresTranslation = false;
          }
          this.model = new ReservationModel({
            ...this.editObject,
            hide_lot: this.editObject.lot ? this.editObject.lot.hide_lot : false,
            use_images_for_auction_gallery: this.editObject.lot ? this.editObject.lot.use_images_for_auction_gallery : false,
            auction_date_uuid: this.editObject.lot ? this.editObject.lot.auction_date_uuid : null,
            auction_collection_uuid: this.editObject.lot ? this.editObject.lot.auction_collection_uuid : null,
            collection_info_address_1: this.editObject.inventory.collection_info_address_1,
            collection_info_address_2: this.editObject.inventory.collection_info_address_2,
            collection_info_state: this.editObject.inventory.collection_info_state,
            collection_info_city: this.editObject.inventory.collection_info_city,
            collection_info_zip_code: this.editObject.inventory.collection_info_zip_code,
            collection_info_phone: this.editObject.inventory.collection_info_phone,
            collection_info_first_name: this.editObject.inventory.collection_info_first_name,
            collection_info_last_name: this.editObject.inventory.collection_info_last_name,
            collection_info_email: this.editObject.inventory.collection_info_email,
            collection_info_notes: this.editObject.inventory.collection_info_notes,
            collection_info_enabled: this.editObject.inventory.collection_info_enabled,
          });
          if (this.enableContinuousAuction) {
            this.saleModel = new SaleModel({
              uuid: this.editObject.auction.uuid,
              is_initial_deposit_amount: this.editObject.auction.is_initial_deposit_amount,
              initial_deposit_amount: this.editObject.auction.initial_deposit_amount,
              require_credit_card_verification: this.editObject.auction.require_credit_card_verification,
              require_identity_verification: this.editObject.auction.require_identity_verification,
              manual_bidder_approval: this.editObject.auction.manual_bidder_approval,
            })
          }
        }
      }
    },
  }
</script>

<style lang="scss" scoped>
  .hide-lot-checkbox {
    max-height: 70.39px;
    display: flex;
    align-items: self-end;
  }
</style>
